import { useEffect, useMemo, useState } from "react";
import { Search, Loader2, CarFront } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { FUELS, BODIES } from "@/lib/vehicles";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { VehicleCard } from "@/components/site/VehicleCard";
import { useVehicles } from "@/components/site/VehiclesSection";

const sel = "rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none focus:border-[#2F7BFF] [&>option]:bg-[#0B1224]";
const SORTS = { newest: () => 0, year_desc: (a, b) => b.year - a.year, km_asc: (a, b) => a.mileage_km - b.mileage_km };

export default function VehiclesPage() {
  const { t } = useLang();
  const tv = t.vehicles;
  const items = useVehicles();
  const [q, setQ] = useState("");
  const [make, setMake] = useState("");
  const [body, setBody] = useState("");
  const [fuel, setFuel] = useState("");
  const [sort, setSort] = useState("newest");
  useEffect(() => { window.scrollTo(0, 0); document.title = `${tv.eyebrow} · Autovantis`; }, [tv.eyebrow]);

  const makes = useMemo(() => [...new Set((items || []).map((v) => v.make))].sort(), [items]);
  const shown = useMemo(() => (items || [])
    .filter((v) => (!make || v.make === make) && (!body || v.body_type === body) && (!fuel || v.fuel === fuel) &&
      `${v.make} ${v.model} ${v.variant || ""}`.toLowerCase().includes(q.toLowerCase()))
    .sort(SORTS[sort]), [items, q, make, body, fuel, sort]);

  return (
    <div className="min-h-screen bg-[#060A14] text-white">
      <Header />
      <main data-testid="vehicles-page" className="mx-auto max-w-7xl px-5 pb-24 pt-14 lg:px-8">
        <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">{tv.title}</h1>
        <p className="mt-4 max-w-2xl text-slate-300">{tv.subtitle}</p>
        <p data-testid="vehicles-b2b-note" className="mt-3 inline-flex rounded-full border border-[#2F7BFF]/30 bg-[#2F7BFF]/10 px-4 py-1.5 text-xs font-semibold text-[#7FB2FF]">{tv.b2bNote}</p>
        <div className="mt-10 flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#0B1224] p-4 lg:flex-row lg:items-center">
          <label className="relative block flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="vehicles-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={tv.searchPlaceholder} className={`${sel} w-full pl-11`} />
          </label>
          <div className="grid grid-cols-2 gap-3 sm:flex">
            <select data-testid="vehicles-filter-make" className={sel} value={make} onChange={(e) => setMake(e.target.value)}>
              <option value="">{tv.allMakes}</option>{makes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select data-testid="vehicles-filter-body" className={sel} value={body} onChange={(e) => setBody(e.target.value)}>
              <option value="">{tv.allBodies}</option>{BODIES.map((b) => <option key={b} value={b}>{tv.body[b]}</option>)}
            </select>
            <select data-testid="vehicles-filter-fuel" className={sel} value={fuel} onChange={(e) => setFuel(e.target.value)}>
              <option value="">{tv.allFuels}</option>{FUELS.map((f) => <option key={f} value={f}>{tv.fuel[f]}</option>)}
            </select>
            <select data-testid="vehicles-sort" className={sel} value={sort} onChange={(e) => setSort(e.target.value)}>
              {Object.keys(SORTS).map((s) => <option key={s} value={s}>{tv.sort[s]}</option>)}
            </select>
          </div>
        </div>
        <p data-testid="vehicles-count" className="mt-6 text-sm text-slate-400">{items ? `${shown.length} ${tv.results}` : ""}</p>
        {items === null ? (
          <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#2F7BFF]" /></div>
        ) : shown.length === 0 ? (
          <div data-testid="vehicles-empty" className="flex h-64 flex-col items-center justify-center gap-3 text-slate-400"><CarFront className="h-10 w-10" />{tv.empty}</div>
        ) : (
          <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{shown.map((v) => <VehicleCard key={v.id} v={v} />)}</div>
        )}
      </main>
      <Footer />
    </div>
  );
}
